import React, { useState } from 'react';
import { Avatar, IconButton, Tooltip } from '@mui/material';
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import { MarkdownContent } from './MarkdownContent';
import { stripExtraTags } from '@common/domain/tag-filter';
import { Persona } from '../../lib/api/Persona';
import { ClassificationBadge, SafetyRoute } from '../ClassificationBadge/ClassificationBadge';
import { SubgraphModal } from '../Graph';
import { Sources } from '../../lib/models/Chat';

export interface EvalMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    sources?: Sources;
    seeds?: string[];
    retrievedChunkIds?: string[];
    usedChunkIds?: string[];
    isStreaming?: boolean;
    safetyRoute?: SafetyRoute;
    rationale?: string;
    turnNumber?: number;
}

interface EvalChatBubbleProps {
    message: EvalMessage;
    persona?: Persona | null;
    evalPersonaName?: string;
}

export const EvalChatBubble: React.FC<EvalChatBubbleProps> = ({ message, persona, evalPersonaName }) => {
    const [graphModalOpen, setGraphModalOpen] = useState(false);

    return (
        <>
            <div className={`message-wrapper ${message.role}`}>
                <div className="message-avatar">
                    {message.role === 'user' ? (
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'var(--color-sage)' }}>
                            {evalPersonaName?.charAt(0) || 'U'}
                        </Avatar>
                    ) : (
                        <Avatar
                            src={persona?.imageURL || persona?.avatar_url}
                            sx={{ width: 32, height: 32, bgcolor: 'var(--color-accent)' }}
                        >
                            {persona?.name?.charAt(0) || 'A'}
                        </Avatar>
                    )}
                </div>
                <div className="message-content">
                    <div className="message-bubble">
                        {message.role === 'assistant' ? (
                            <MarkdownContent
                                content={stripExtraTags(message.content) + (message.isStreaming ? ' <span class="streaming-cursor">▊</span>' : '')}
                                sources={message.sources || {}}
                            />
                        ) : (
                            <>
                                {stripExtraTags(message.content)}
                                {message.isStreaming && <span className="streaming-cursor">▊</span>}
                            </>
                        )}
                    </div>

                    {/* Metadata/Indicators */}
                    <div className="message-metadata" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                        {/* Graph icon for Assistant turns */}
                        {message.role === 'assistant' && message.seeds && message.seeds.length > 0 && (
                            <Tooltip title="View Knowledge Graph" placement="top">
                                <IconButton
                                    size="small"
                                    onClick={() => setGraphModalOpen(true)}
                                    sx={{ color: "var(--color-text-muted)", padding: 0 }}
                                >
                                    <ScatterPlotIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Safety badge and Rationale for User turns */}
                        {message.role === 'user' && (
                            <>
                                {message.safetyRoute && (
                                    <ClassificationBadge safetyRoute={message.safetyRoute} compact />
                                )}
                                {message.rationale && (
                                    <div className="rationale-text">
                                        <strong>Rationale:</strong> {message.rationale}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Subgraph Modal */}
            {message.seeds && message.seeds.length > 0 && (
                <SubgraphModal
                    open={graphModalOpen}
                    onClose={() => setGraphModalOpen(false)}
                    seedIds={message.seeds}
                    retrievedIds={message.retrievedChunkIds}
                    usedIds={message.usedChunkIds}
                    tenantId={persona?.tenantId || 'chip'}
                    title="Knowledge Graph for This Response"
                />
            )}
        </>
    );
};
